import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '../utils/errorHandler';

interface NoteManagementProps {
    onMenuSelect: (menuId: string) => void;
    tenantName: string;
    currentUser: any;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

interface NoteData {
    note_type: string;
    content: string;
}

const NOTE_TYPES = [
    { id: 'significant_accounting_policies', label: '重要な会計方針' },
    { id: 'change_in_accounting_policies', label: '会計方針の変更' },
    { id: 'important_events', label: '重要な後発事象' },
    { id: 'other_notes', label: 'その他の注記' },
];

const NoteManagement: React.FC<NoteManagementProps> = ({
    onMenuSelect,
    tenantName,
    currentUser,
    apiFetch,
    currentTenantId,
}) => {
    const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1);
    const [notes, setNotes] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);

    const fetchNotes = async () => {
        if (!currentTenantId) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/api/notes?fiscal_year=${selectedFiscalYear}`);
            if (res.ok) {
                const data = await res.json();
                const noteMap: Record<string, string> = {};
                data.forEach((n: any) => {
                    noteMap[n.note_type] = n.content || '';
                });
                setNotes(noteMap);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, [selectedFiscalYear, currentTenantId]);

    const handleSave = async (noteType: string) => {
        setSaving(true);
        setStatusMessage(null);
        try {
            const res = await apiFetch(`/api/notes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fiscal_year: selectedFiscalYear,
                    note_type: noteType,
                    content: notes[noteType] || ''
                })
            });

            if (!res.ok) throw new Error("保存に失敗しました");
            setStatusMessage(`${NOTE_TYPES.find(t => t.id === noteType)?.label}を保存しました`);
            setTimeout(() => setStatusMessage(null), 3000);
        } catch (e: any) {
            alert(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', backgroundColor: '#fff', border: '2px solid #ccc' }}>
            <div style={{ marginBottom: '20px', backgroundColor: '#f0f0f0', padding: '10px' }}>
                <strong>会計年度: </strong>
                <select
                    value={selectedFiscalYear}
                    onChange={(e) => setSelectedFiscalYear(Number(e.target.value))}
                    style={{ padding: '4px' }}
                >
                    {[...Array(5)].map((_, i) => {
                        const y = new Date().getFullYear() - 2 + i;
                        return <option key={y} value={y}>{y}年度</option>;
                    })}
                </select>
                <button onClick={fetchNotes} style={{ marginLeft: '10px', padding: '4px 8px' }}>更新</button>
            </div>

            {statusMessage && <div style={{ marginBottom: '10px', color: 'green', fontWeight: 'bold' }}>{statusMessage}</div>}

            {NOTE_TYPES.map((type) => (
                <div key={type.id} style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{ fontWeight: 'bold' }}>{type.label}</label>
                        <button
                            onClick={() => handleSave(type.id)}
                            disabled={saving}
                            style={{ padding: '2px 8px', cursor: 'pointer' }}
                        >
                            保存
                        </button>
                    </div>
                    <textarea
                        value={notes[type.id] || ''}
                        onChange={(e) => setNotes(prev => ({ ...prev, [type.id]: e.target.value }))}
                        style={{ width: '100%', height: '100px', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                        placeholder={`${type.label}を入力してください...`}
                    />
                </div>
            ))}
        </div>
    );
};

export default NoteManagement;
